/**
 * MONSTER TOWN — PIECE DRAWER. OWNED BY THE BUILD CREW (src/build/**).
 *
 * The kit box: big picture tiles grouped into picture tabs. This is the only
 * place a kid chooses what to build with, so:
 *
 *  - every control is 88 px+ (var(--touch-min)), 3 px ink border, chunky shadow;
 *  - no words anywhere (LAW #4) — tabs and tiles are drawn icons;
 *  - the selected tile is loudly highlighted so it matches the ghost in the world;
 *  - rotate and trash live here, next to the pieces they act on;
 *  - the drawer collapses to a single row so it never hides the town, and the
 *    collapse button is as big as everything else. No dead ends.
 *
 * ============ NOTHING IN HERE MAY EVER BE OFF-SCREEN (fixed r1) ============
 * The first version laid tabs and tiles out in `overflow-x: auto` strips and
 * trusted a swipe to reach the rest. Measured on the three real profiles that
 * meant 18-21 of 28 furniture tiles sat past the right edge of the screen, and
 * on the 1024-wide hand-me-down and in portrait the *tabs* themselves ran off
 * at x=1030..1118. The swipe that was supposed to save it was being cancelled
 * by the document-level touchmove guard in src/ui/touch.ts, so the strips could
 * not move at all. A kid could not reach the bed. That is the whole "decorate
 * your house" pillar, unreachable.
 *
 * The rule now is that reachability may not depend on scrolling:
 *   - TABS WRAP. The tool buttons and every tab live in one `flex-wrap` row, so
 *     they take as many lines as they need and every tab is always on screen.
 *   - TILES PAGE. The tile area is measured from its own bounding rect, filled
 *     with whole rows of whole tiles, and flanked by two 88 px arrows that wrap
 *     around forever. No disabled buttons, no dead ends, no precision flick.
 *   - The list is FILTERED to the room you are standing in, so it is short
 *     enough to page through in the first place.
 * (touch.ts now also exempts scrollers, so a swipe works too — but nothing
 * depends on it.)
 * ==========================================================================
 *
 * TAB CONTENT IS DERIVED FROM THE REGISTRY, not hard-coded: when the interiors
 * or venue crew drops in furniture, its tab appears by itself. An empty tab is
 * hidden rather than shown as a disappointment.
 *
 * ====== IT OPENS ON A FULL TAB, AND A TAB SHOWS A REAL PIECE (fixed r2) ======
 * Measured on a fresh save, real hammer tap, three scenes: ONE tile on screen,
 * in a drawer eating 27% of the canvas. Boot armed `block_wood`, the drawer
 * dutifully revealed the tab holding it, and the `blocks` tab holds exactly one
 * def — so a kid's first ever sight of build mode was one brown cube and a row
 * of nine abstract glyphs, over a quarter of the screen of cream. The kit was
 * all there (town: 6 garden, 9 decor; house: 6 furniture, 14 decor; shop: 12
 * fittings) and every bit of it was behind a tab a pre-reader has no reason to
 * press. Three changes, all in this file:
 *
 *   1. OPENING TAB. Until a kid taps a tab or a tile themselves, the drawer
 *      picks the fullest kid-facing tab in the room it is standing in and arms
 *      its first piece. Walk into the house and it opens on furniture; walk
 *      into the shop and it opens on the shop fittings.
 *   2. TABS SHOW A PIECE, NOT A SYMBOL. A tab is drawn as an actual thing it
 *      holds — a flower, a chair, an ice cream tub — instead of a category
 *      glyph. "Purple star" and "lollipop" told a non-reader nothing; a picture
 *      of a chair tells them everything, and it needs no preview to explain it.
 *   3. THE DRAWER IS ONLY AS TALL AS ITS CONTENT. Rows are derived from how
 *      many pieces the tab actually has, so a short tab is one 88px row rather
 *      than two rows of empty cream.
 * ==========================================================================
 */

import './build.css'
import { bus } from '../core/events'
import { wallet, priceOf } from '../core/economy'
import { allPlaceables, type AnyPlaceableDef, type PlaceableCategory } from '../core/placeable'
import { world } from '../core/scene'
import type { PlaceableId, SceneId } from '../core/types'
import { app, type GameContext } from '../app/context'
import { drawBuildToolIcon, drawCategoryIcon, drawPieceIcon } from './icons'
import {
  buildTool,
  carried,
  rotateSelection,
  selectPlaceable,
  selectedPlaceable,
  setBuildTool,
  toggleDeleteTool,
} from './state'
import { cancelCarry } from './gestures'

interface Tab {
  id: string
  icon: PlaceableCategory
  cats: readonly PlaceableCategory[]
  /**
   * Where this tab's pieces go when there are too few of them to be worth a tab
   * of their own. See `resolveTabs()`.
   */
  foldInto?: string
}

/**
 * Kid-legible groups. Ordering is "what you build a house out of" first.
 *
 * These are deliberately NARROW. One fat `furniture` tab used to hold
 * furniture + bed + kitchen + bath + appliance + venueFixture, which is how a
 * bedroom ended up offering seventeen ice-cream-shop fittings before it offered
 * the bed. A tab should answer one question a kid has.
 */
const TAB_DEFS: readonly Tab[] = [
  { id: 'walls', icon: 'wall', cats: ['wall', 'door', 'window'] },
  { id: 'roofs', icon: 'roof', cats: ['roof'], foldInto: 'walls' },
  { id: 'floors', icon: 'floor', cats: ['floor'], foldInto: 'paths' },
  { id: 'paths', icon: 'path', cats: ['path', 'stair'] },
  { id: 'blocks', icon: 'block', cats: ['block'], foldInto: 'walls' },
  { id: 'fences', icon: 'fence', cats: ['fence'], foldInto: 'paths' },
  { id: 'garden', icon: 'plant', cats: ['plant', 'tree', 'water'] },
  { id: 'furniture', icon: 'furniture', cats: ['furniture', 'bed'], foldInto: 'decor' },
  { id: 'kitchen', icon: 'kitchen', cats: ['kitchen', 'bath', 'appliance'], foldInto: 'furniture' },
  { id: 'decor', icon: 'decor', cats: ['decor', 'light', 'toy', 'townProp'] },
  { id: 'shop', icon: 'venueFixture', cats: ['venueFixture'] },
]

/**
 * ============ NO TAB IS EVER ONE LONELY TILE (fixed r3) ============
 * Round-3 critic, minor: "Four of the nine categories hold a single piece, which
 * renders as one lonely tile floating in a full-width tray and reads as 'there
 * is nothing here'."
 *
 * A tab is a promise that there is a choice behind it. Below this many pieces
 * there is no choice, so the tab is not offered: its pieces are folded into the
 * related tab named by `foldInto` and it disappears. One roof joins the walls;
 * a lone fence joins the paths.
 *
 * It is derived from the LIVE registry per room, so it heals itself in both
 * directions: as crews drop content in (or as a kid unlocks it) a folded tab
 * pops back out on its own, and a thin room never shows a bare shelf. No content
 * is ever unreachable — a folded tab's pieces are all in the tab it folded into.
 * ====================================================================
 */
const MIN_TAB_TILES = 2

/**
 * The tab a kid is offered FIRST in a room they have not chosen a tab in.
 * Ordered by "what is there most of, and what is most obviously fun": the
 * garden and the furniture sell the kit far better than a tab holding one cube.
 * Whatever is first in this list and actually has pieces here wins.
 */
const OPENING_TABS: readonly string[] = ['furniture', 'garden', 'shop', 'decor', 'kitchen', 'walls', 'paths', 'floors']
/** Below this a tab is not worth opening on — it would look empty again. */
const OPENING_MIN = 3

/** Tile grid geometry. Must match build.css. */
const TILE_PX = 88
const GAP_PX = 10
const TILE_ROWS = 2

let rootEl: HTMLDivElement | null = null
let canvasEl: HTMLCanvasElement | null = null
let lastReserve = -1
let tabsEl: HTMLDivElement | null = null
let tilesEl: HTMLDivElement | null = null
let pagerEls: { prev: HTMLButtonElement; next: HTMLButtonElement } | null = null
let activeTab = TAB_DEFS[0]?.id ?? 'walls'
let page = 0
let open = true
let perPage = TILE_ROWS * 4
/**
 * True once a kid has picked a tab or a tile with their own finger. Until then
 * the drawer is free to open itself on the fullest tab in the room; after it,
 * nothing ever moves their selection out from under them.
 */
let kidChose = false

// ---------------------------------------------------------------------------
// dom helpers
// ---------------------------------------------------------------------------

function iconCanvas(size: number, paint: (c: CanvasRenderingContext2D, s: number) => void): HTMLCanvasElement {
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const cv = document.createElement('canvas')
  cv.width = Math.round(size * dpr)
  cv.height = Math.round(size * dpr)
  cv.style.width = `${size}px`
  cv.style.height = `${size}px`
  const c = cv.getContext('2d')
  if (c !== null) {
    c.scale(dpr, dpr)
    paint(c, size)
  }
  return cv
}

function pill(
  className: string,
  paint: (c: CanvasRenderingContext2D, s: number) => void,
  onTap: () => void,
  label: string,
): HTMLButtonElement {
  const b = document.createElement('button')
  b.className = `mt-pill ${className}`.trim()
  b.setAttribute('aria-label', label)
  b.appendChild(iconCanvas(72, paint))
  // pointerup, not click: no 300 ms wait, and no double-tap can ever be needed.
  b.addEventListener('pointerup', (e) => {
    e.preventDefault()
    e.stopPropagation()
    onTap()
  })
  b.addEventListener('pointerdown', (e) => e.stopPropagation())
  return b
}

// ---------------------------------------------------------------------------
// content
// ---------------------------------------------------------------------------

/**
 * WHICH PIECES BELONG IN THE PLACE YOU ARE STANDING IN.
 *
 * Scale alone is not enough: every venue fitting is `scale: 'interior'`, so a
 * bedroom was being offered five ice cream tubs, three cooking pots, a topping
 * jar and a serving counter. Content already says what it is — venue fittings
 * carry the `venue` tag — so the drawer honours it: shop equipment shows up in
 * a shop, and everywhere else you get the things that belong there.
 *
 * Deliberately one-way: a venue also gets the ordinary rugs, lamps and plants,
 * because decorating your shop is the point.
 */
const FITTINGS: readonly { tag: string; scenePrefix: string }[] = [
  { tag: 'venue', scenePrefix: 'venue' },
  { tag: 'shop', scenePrefix: 'shop' },
]

function belongsHere(def: AnyPlaceableDef, sceneId: SceneId | null): boolean {
  const scene = sceneId === null ? '' : String(sceneId)
  for (const f of FITTINGS) {
    if (def.tags?.includes(f.tag) !== true) continue
    if (!scene.startsWith(f.scenePrefix)) return false
  }
  return true
}

/** Pieces that fit the scene we are standing in and that the kid may place. */
function availableDefs(): AnyPlaceableDef[] {
  const sceneId = world.activeSceneId
  const scale = sceneId === null ? 'exterior' : world.require(sceneId).def.grid.scale
  return allPlaceables().filter(
    (d) =>
      (d.scale === scale || d.scale === 'both') &&
      wallet.isUnlocked(d.id) &&
      belongsHere(d, sceneId),
  )
}

function defsForTab(tab: Tab, defs: readonly AnyPlaceableDef[]): AnyPlaceableDef[] {
  return defs.filter((d) => tab.cats.includes(d.category))
}

/**
 * The tabs this room actually gets, with thin ones folded away (see
 * `MIN_TAB_TILES`). Cheap enough to run per repaint — eleven tabs over a few
 * dozen defs — and deriving it every time is what makes it self-healing.
 */
function resolveTabs(defs: readonly AnyPlaceableDef[]): Tab[] {
  const cats = new Map<string, PlaceableCategory[]>()
  for (const t of TAB_DEFS) cats.set(t.id, [...t.cats])

  const countOf = (id: string): number => {
    const list = cats.get(id)
    if (list === undefined || list.length === 0) return 0
    return defs.filter((d) => list.includes(d.category)).length
  }

  const folded = new Set<string>()
  for (const t of TAB_DEFS) {
    if (t.foldInto === undefined) continue
    const n = countOf(t.id)
    if (n === 0 || n >= MIN_TAB_TILES) continue
    const target = cats.get(t.foldInto)
    const mine = cats.get(t.id)
    if (target === undefined || mine === undefined) continue
    // Never fold into a tab that is itself empty here — the pieces would end up
    // behind a tab that is not on screen.
    if (countOf(t.foldInto) === 0) continue
    target.push(...mine)
    folded.add(t.id)
  }

  const out: Tab[] = []
  for (const t of TAB_DEFS) {
    if (folded.has(t.id)) continue
    const list = cats.get(t.id) ?? []
    out.push({ id: t.id, icon: t.icon, cats: list })
  }
  return out
}

/** The resolved tabs for the room we are standing in, right now. */
function tabsHere(defs?: readonly AnyPlaceableDef[]): Tab[] {
  return resolveTabs(defs ?? availableDefs())
}

function currentTab(defs?: readonly AnyPlaceableDef[]): Tab | undefined {
  return tabsHere(defs).find((t) => t.id === activeTab)
}

/**
 * How many tiles fit, measured from the tile strip's OWN bounding rect.
 * Never window.innerWidth: on an older iPad the window and the canvas disagree
 * and every layout decision made from `window` is wrong by a slot.
 */
function measureCols(): number {
  if (tilesEl === null) return 4
  const w = tilesEl.getBoundingClientRect().width
  if (w <= 0) return 0
  return Math.max(1, Math.floor((w + GAP_PX) / (TILE_PX + GAP_PX)))
}

/**
 * How many rows this tab is worth. Derived from the CONTENT, not fixed:
 *
 *  - a tab with four pieces on a six-column strip is ONE row, so the drawer is
 *    88px shorter than it used to be instead of carrying a row of empty cream;
 *  - a full tab gets the second row, so the whole kit is on screen at once;
 *  - a NARROW strip gets a third. Portrait is short on width and long on
 *    height: at two columns a two-row page holds four pieces and the decor tab
 *    becomes a seven-page flick-through. One more 88px row there roughly halves
 *    the paging.
 *
 * It is a function of the tab (never of the page), so paging inside a tab can
 * never change the layout under the finger doing the paging.
 */
function rowsFor(cols: number, total: number): number {
  const max = cols >= 3 ? TILE_ROWS : TILE_ROWS + 1
  return Math.max(1, Math.min(max, Math.ceil(total / Math.max(1, cols))))
}

function measureGrid(): { cols: number; rows: number; perPage: number } {
  const cols = measureCols()
  if (cols === 0) return { cols: 1, rows: 1, perPage }
  const tab = currentTab()
  const total = tab === undefined ? 0 : defsForTab(tab, availableDefs()).length
  const rows = rowsFor(cols, total)
  return { cols, rows, perPage: cols * rows }
}

function measurePerPage(): number {
  return measureGrid().perPage
}

/**
 * SETTLE THE LAYOUT, DON'T SHIP THE FIRST GUESS.
 *
 * `measureCols()` reads the tile strip's real width, and that width depends on
 * whether the pager column is on screen — which depends on how many pages there
 * are — which depends on how many columns fit. Measuring once meant the hand-
 * me-down iPad laid the strip out for FIVE columns while the pager was still
 * boxed, found the tab fitted on one page, dropped the pager, and left six
 * pieces spread over two rows in a strip that was now wide enough for one. That
 * is 88px of empty cream on a 768px screen — the exact defect this round is
 * fixing, reintroduced by a stale measurement.
 *
 * One extra pass is enough: hiding the pager only ever makes the strip WIDER,
 * so the second measurement is stable. The pass counter is a hard stop either
 * way — a layout loop in a kid's build drawer is not an acceptable failure.
 */
function paintTiles(pass = 0): void {
  if (tilesEl === null) return
  const tab = currentTab()
  if (tab === undefined) return
  const all = defsForTab(tab, availableDefs())
  const grid = measureGrid()
  perPage = grid.perPage
  const pages = Math.max(1, Math.ceil(all.length / perPage))
  if (page >= pages) page = 0
  if (page < 0) page = pages - 1

  /*
   * EVEN ROWS, CENTRED. Six pieces in a five-wide strip laid out as 5 + 1: a
   * full row and one lonely tile with four empty slots beside it, which is the
   * "empty cream" this round is fixing wearing a smaller hat. The tiles go in an
   * explicit grid of ceil(shown / rows) columns instead — 3 + 3 — centred in the
   * shelf, so a page of pieces always reads as a tidy block.
   *
   * An EXPLICIT column count, not flex wrapping, on purpose: `measureCols()`
   * reads this element's width, so anything that narrows the element to fit its
   * contents feeds back into the next measurement. A grid container still
   * stretches to the full strip, so the measurement stays honest.
   */
  const shown = Math.min(perPage, Math.max(0, all.length - page * perPage))
  const cols = Math.max(1, Math.ceil(shown / Math.max(1, grid.rows)))
  tilesEl.style.gridTemplateColumns = `repeat(${cols}, ${TILE_PX}px)`

  tilesEl.innerHTML = ''
  const selected = selectedPlaceable()
  const start = page * perPage
  for (const def of all.slice(start, start + perPage)) {
    const tile = pill(
      'mt-tile',
      (c, s) => drawPieceIcon(c, s, def),
      () => {
        // Picking a piece obviously means "I want to build with this", so it
        // also gets you out of trash mode. No way to be stuck wondering why
        // tapping the ground does nothing.
        kidChose = true
        setBuildTool('place')
        selectPlaceable(def.id)
        paintTiles()
      },
      def.devName,
    )
    tile.dataset['selected'] = String(selected === def.id)
    const cost = priceOf(def.id)
    /*
     * "CAN THIS ACTUALLY BE PUT DOWN RIGHT NOW", as an attribute.
     *
     * The drawer already draws the answer — a price badge means not yet — but
     * only a reader can use a badge. The first-run tutorial has to point a paw
     * at a piece a kid can really place: it pointed at whatever tile came first,
     * and against the deployed build that was a piece they did not own, so every
     * ground tap after it was politely refused and the beat sat there for ever.
     * Nothing is disabled by this (a kid may still tap a locked piece and get
     * the shop's "not yet" jiggle) — it is a label for anything that needs to
     * choose a tile on a kid's behalf.
     */
    tile.dataset['free'] = String(wallet.canPlace(def.id))
    if (cost > 0 && !wallet.owns(def.id)) {
      const badge = document.createElement('span')
      badge.className = 'mt-tile-cost'
      badge.textContent = String(cost)
      tile.appendChild(badge)
    }
    tilesEl.appendChild(tile)
  }

  // The arrows are only USABLE when there is somewhere to go — a 3-year-old is
  // never looking at a button that does nothing — and they WRAP, so tapping
  // next on the last page lands on the first and neither is a dead end.
  //
  // They keep their space when they are not usable (visibility, not display).
  // That is not cosmetic: `measurePerPage()` reads the strip's width, and
  // taking 196 px of arrows OUT of the row after measuring made page 0 lay out
  // for six columns and page 1 for four, so a tab's pages silently overlapped.
  if (pagerEls !== null) {
    const usable = String(pages <= 1)
    pagerEls.prev.dataset['off'] = usable
    pagerEls.next.dataset['off'] = usable
  }
  if (rootEl !== null) {
    rootEl.dataset['page'] = String(page)
    rootEl.dataset['pages'] = String(pages)
  }

  // Now that the pager's box has been resolved, check the strip again. See the
  // note above this function. Bounded to one repaint, always.
  if (pass === 0 && measurePerPage() !== perPage) paintTiles(1)
}

function flipPage(delta: number): void {
  page += delta
  paintTiles()
}

/**
 * A tab is drawn as a PIECE IT HOLDS, not as a category symbol.
 *
 * The symbols were the defect: nine glyphs, and a pre-reader could not tell
 * what "purple star", "lollipop" or "two yellow tiles" would put in their town.
 * A picture of a flower, a chair or an ice cream tub is self-explanatory and
 * needs no words, no preview and no grown-up. The category glyph stays as the
 * fallback for a tab whose contents have not loaded yet.
 */
function tabPaint(tab: Tab, defs: readonly AnyPlaceableDef[]): (c: CanvasRenderingContext2D, s: number) => void {
  const mine = defsForTab(tab, defs)
  const rep = mine.find((d) => d.category === tab.icon) ?? mine[0]
  if (rep === undefined) return (c, s) => drawCategoryIcon(c, s, tab.icon)
  return (c, s) => drawPieceIcon(c, s, rep)
}

function paintTabs(): void {
  if (tabsEl === null) return
  tabsEl.innerHTML = ''
  const defs = availableDefs()
  let firstNonEmpty: string | null = null

  for (const tab of tabsHere(defs)) {
    if (defsForTab(tab, defs).length === 0) continue
    if (firstNonEmpty === null) firstNonEmpty = tab.id
    const b = pill(
      '',
      tabPaint(tab, defs),
      () => {
        kidChose = true
        if (activeTab !== tab.id) page = 0
        activeTab = tab.id
        paintTabs()
        paintTiles()
      },
      tab.id,
    )
    b.dataset['on'] = String(activeTab === tab.id)
    tabsEl.appendChild(b)
  }

  // If the active tab vanished (scene change, nothing unlocked), fall back so a
  // kid can never end up staring at an empty drawer.
  const current = currentTab(defs)
  if (firstNonEmpty !== null && (current === undefined || defsForTab(current, defs).length === 0)) {
    activeTab = firstNonEmpty
    page = 0
    paintTabs()
  }
}

/** Jump to the tab AND the page holding a piece, so the highlighted tile always
 *  matches the ghost in the world. A kid must never have to hunt for what they
 *  picked — including "it is on page 2 and nothing says so". */
function revealSelection(): void {
  const id = selectedPlaceable()
  if (id === null) return
  const def = allPlaceables().find((d) => d.id === id)
  if (def === undefined) return
  const here = availableDefs()
  const tab = tabsHere(here).find((t) => t.cats.includes(def.category))
  if (tab === undefined) return
  if (tab.id !== activeTab) {
    activeTab = tab.id
    page = 0
    paintTabs()
  }
  const all = defsForTab(tab, here)
  const at = all.findIndex((d) => d.id === id)
  if (at < 0) return
  perPage = measurePerPage()
  page = Math.floor(at / Math.max(1, perPage))
}

/**
 * OPEN ON SOMETHING WORTH LOOKING AT.
 *
 * Boot arms `block_wood`, whose tab holds exactly one piece, so build mode used
 * to open on a single brown cube. Until a kid has chosen for themselves, pick
 * the fullest kid-facing tab that this room actually has and arm its first
 * piece — the selection and the drawer then agree, and the ghost in the world
 * shows something a kid would want to put down.
 *
 * Returns true if it moved the selection.
 */
function openOnFullTab(): boolean {
  if (kidChose) return false
  const defs = availableDefs()
  const tabs = tabsHere(defs)
  let best: { tab: Tab; list: AnyPlaceableDef[] } | null = null
  for (const id of OPENING_TABS) {
    const tab = tabs.find((t) => t.id === id)
    if (tab === undefined) continue
    const list = defsForTab(tab, defs)
    if (list.length < OPENING_MIN) continue
    best = { tab, list }
    break
  }
  if (best === null) {
    // Nothing rich enough here: fall back to whatever is biggest, so the drawer
    // is never opened on a one-tile tab when a fuller one exists.
    for (const tab of tabs) {
      const list = defsForTab(tab, defs)
      if (best === null || list.length > best.list.length) best = { tab, list }
    }
  }
  const first = best?.list[0]
  if (best === null || first === undefined) return false
  activeTab = best.tab.id
  page = 0
  selectPlaceable(first.id)
  return true
}

function paintVisibility(): void {
  if (rootEl === null) return
  rootEl.dataset['visible'] = String(app.mode === 'build')
  rootEl.dataset['open'] = String(open)
  // The whole drawer goes red in trash mode. A 3-year-old should not have to
  // notice one small button to understand why taps are taking things away.
  rootEl.dataset['tool'] = buildTool()
  // ...and teal while a piece is in the air, for exactly the same reason. This
  // is the "you are holding something" gate the critic could not find.
  rootEl.dataset['carry'] = String(carried() !== null)
  // The green tick is on screen only when there is something to be finished
  // with, so it is never one more button to wonder about.
  rootEl.dataset['done'] = String(buildTool() === 'delete' || carried() !== null)
  reportReserve()
}

/**
 * HOW MUCH OF THE CANVAS THE DRAWER IS EATING.
 *
 * The monster crew lifts the camera framing by half of this, so the piece a kid
 * is placing is never behind the drawer. Measured from the drawer's OWN rect
 * against the CANVAS rect — never `window`, which on an older iPad disagrees
 * with both.
 */
function reportReserve(): void {
  const canvas = canvasEl
  if (canvas === null) return
  const box = canvas.getBoundingClientRect()
  if (box.height <= 0) return
  let fraction = 0
  if (rootEl !== null && rootEl.offsetParent !== null) {
    const r = rootEl.getBoundingClientRect()
    // Only the part that actually overlaps the canvas counts.
    const overlap = Math.max(0, Math.min(box.bottom, r.bottom) - Math.max(box.top, r.top))
    fraction = Math.max(0, Math.min(0.6, overlap / box.height))
  }
  if (Math.abs(fraction - lastReserve) < 0.005) return
  lastReserve = fraction
  bus.emit('build:reserve', { fraction })
}

// ---------------------------------------------------------------------------
// mount
// ---------------------------------------------------------------------------

function mount(hud: HTMLElement): void {
  const existing = document.getElementById('mt-build')
  if (existing !== null) existing.remove()

  const root = document.createElement('div')
  root.id = 'mt-build'

  const bar = document.createElement('div')
  bar.className = 'mt-build-row'

  /*
   * WHY THE PIECES AND THE TOOLS SHARE A ROW (fixed r1).
   *
   * The drawer used to be three bands: a row holding rotate/trash/collapse, a
   * row of tabs, and a row of tiles. On an iPad the tab strip could not fit
   * beside the tools, so it wrapped underneath them — and the tool row was then
   * three buttons and 85% empty cream, while the tile row was ONE tile and 89%
   * empty cream. The whole thing ate 39% of the screen to show four controls.
   *
   * Now: the tabs own a row of their own (they fit on one line at iPad width),
   * the tools sit at the head of the tile row where the pieces they act on are,
   * and the collapse chevron is a handle on the drawer's corner instead of a
   * button competing for the row. Nothing is smaller, nothing scrolls, and no
   * row can be nearly empty — the tool group is always in the tile row.
   */
  const rotateBtn = pill(
    'mt-tool',
    (c, s) => drawBuildToolIcon(c, s, 'rotate'),
    () => rotateSelection(),
    'Turn the piece',
  )
  const trashBtn = pill(
    'mt-tool',
    (c, s) => drawBuildToolIcon(c, s, 'trash'),
    () => toggleDeleteTool(),
    'Take pieces away',
  )
  trashBtn.dataset['danger'] = 'true'
  trashBtn.dataset['on'] = String(buildTool() === 'delete')

  /**
   * ============ THE WAY OUT OF THE BIN, AND OUT OF A PICKUP (fixed r3) ============
   * Two round-3 majors, one answer: a destructive-feeling state must have a big
   * green way out that is VISIBLE while you are in it.
   *
   *  - "One tap arms it … and from then on every ground tap removes a piece …
   *     the only way out is a second tap on the same trash button; there is no
   *     separate done/cancel and nothing indicates that."
   *  - "a 700ms stationary press … emitted build:carryStart; the very next tap
   *     elsewhere on the ground moved its anchor. No confirmation, no visible
   *     'you are holding something' gate."
   *
   * So the tray grows ONE extra 88 px button whenever the hammer is in a state a
   * kid might want out of, and it is the same button in both cases: a green tick
   * that means "I am done with this". It is not a confirmation dialog, nothing
   * is blocked while it is up, and the old exits still work — it is the exit a
   * pre-reader can SEE.
   * ==============================================================================
   */
  const doneBtn = pill(
    'mt-tool mt-done',
    (c, s) => drawBuildToolIcon(c, s, 'done'),
    () => {
      if (carried() !== null) cancelCarry()
      setBuildTool('place')
      paintVisibility()
    },
    'Finished',
  )
  doneBtn.dataset['ok'] = 'true'

  // The handle stays OUTSIDE the collapsible part, always visible: a control
  // that hides itself is a dead end.
  const collapseBtn = pill(
    'mt-build-collapse',
    (c, s) => drawBuildToolIcon(c, s, open ? 'collapse' : 'expand'),
    () => {
      open = !open
      paintVisibility()
      collapseBtn.replaceChildren(iconCanvas(72, (c, s) => drawBuildToolIcon(c, s, open ? 'collapse' : 'expand')))
      bus.emit('build:paletteToggled', { open })
    },
    'Show or hide the pieces',
  )

  const tabs = document.createElement('div')
  tabs.className = 'mt-build-tabs'

  /**
   * WHERE THE HUD CREW'S UNDO BUTTON LIVES.
   *
   * Undo used to sit in its own detached box in the bottom-left corner, in a
   * different size and a different colour from everything it works on. It docks
   * here instead, at the head of the tile row with rotate and trash — the three
   * things you do to a piece, in one group, beside the pieces.
   *
   * It is in THIS row and not in the tab row on purpose: a 1024-wide iPad fits
   * exactly nine tabs across, and an undo button in that row pushed the whole
   * tab strip onto a second line, which is how the drawer grew a third band that
   * was one button and empty cream — the defect this is fixing.
   *
   * `src/ui/hud.ts` moves its button in here when this node is VISIBLE and takes
   * it back to the corner when it is not (drawer collapsed, or play mode), so
   * undo is never lost and neither crew can break the other by shipping first.
   */
  const undoDock = document.createElement('div')
  undoDock.className = 'mt-build-dock'
  undoDock.id = 'mt-build-dock'

  bar.append(tabs)

  const tileRow = document.createElement('div')
  tileRow.className = 'mt-build-tilerow'
  /*
   * THE PAGER IS A COLUMN, NOT TWO BUTTONS IN THE ROW.
   *
   * Side by side they ate 196px of the row. With undo, rotate and trash also in
   * this row that left a 155px tile strip on a portrait iPad — ONE tile wide,
   * and the decor tab seven pages deep. Stacked, they cost 88px and stand
   * exactly as tall as the tile block they page.
   */
  const pager = document.createElement('div')
  pager.className = 'mt-pager'
  // Two arrows that both said "More pieces" — indistinguishable to anything that
  // cannot see the glyphs, including QA. They say which way they go now. (Words
  // are never on screen; these are labels, not text a kid must read.)
  const prev = pill('mt-page', (c, s) => drawBuildToolIcon(c, s, 'pageUp'), () => flipPage(-1), 'Pieces before')
  const next = pill('mt-page', (c, s) => drawBuildToolIcon(c, s, 'pageDown'), () => flipPage(1), 'More pieces')
  pager.append(prev, next)
  const tiles = document.createElement('div')
  tiles.className = 'mt-build-scroll mt-build-tiles'

  /*
   * TOOLS LIVE IN A TRAY, PIECES LIVE ON A SHELF (fixed r2).
   *
   * Undo, rotate and trash used to sit loose in the tile row with a 3px hairline
   * between them and the first purchasable piece, so a TOOL and a THING YOU PUT
   * IN YOUR TOWN read as siblings — same pill, same size, same row, one thin
   * line. A kid cannot be expected to infer a category boundary from a hairline.
   * The three tools now sit in their own sunken tray with its own border, and
   * the pieces sit on their own shelf, so the two groups are different objects
   * before a single icon is read.
   */
  const toolTray = document.createElement('div')
  toolTray.className = 'mt-toolgroup'
  /*
   * THE BIN IS NOT A NEIGHBOUR OF ROTATE (fixed r3).
   *
   * "'Take pieces away' is 88x88 at cx=186, an 8px gutter from 'Turn the piece'"
   * — eight pixels between the harmless button and the one that removes a kid's
   * work, in a game whose own rule is "no precision required". The two safe
   * tools sit together in the tray; the bin sits on the far side of a fat gap
   * in its own red-bordered box, so reaching one can never land on the other.
   */
  const safeTools = document.createElement('div')
  safeTools.className = 'mt-toolsafe'
  safeTools.append(undoDock, rotateBtn)
  const dangerTools = document.createElement('div')
  dangerTools.className = 'mt-tooldanger'
  dangerTools.append(trashBtn)
  toolTray.append(safeTools, doneBtn, dangerTools)
  tileRow.append(toolTray, tiles, pager)

  root.append(collapseBtn, bar, tileRow)
  hud.appendChild(root)

  rootEl = root
  tabsEl = tabs
  tilesEl = tiles
  pagerEls = { prev, next }

  bus.on('build:toolChanged', (e) => {
    trashBtn.dataset['on'] = String(e.tool === 'delete')
    paintVisibility()
  })
  // The carry gate is painted from the same place as the trash gate: one state,
  // one look, one way out.
  bus.on('build:carryStart', () => paintVisibility())
  bus.on('build:carryEnd', () => paintVisibility())

  // Rotating the iPad changes how many tiles fit. Re-page from the strip's own
  // box, never from `window`.
  if (typeof ResizeObserver !== 'undefined') {
    let last = -1
    const ro = new ResizeObserver(() => {
      const n = measurePerPage()
      if (n === last) return
      last = n
      paintTiles()
    })
    ro.observe(tiles)
    // The drawer's own height drives the camera lift. Watch the drawer itself:
    // its height changes when a tab wraps, when the iPad turns, and when it is
    // collapsed, and every one of those changes what the camera must clear.
    const rootRo = new ResizeObserver(() => reportReserve())
    rootRo.observe(root)
  }

  // Open on a full tab if the kid has not chosen one yet; otherwise go to
  // whatever they have armed. Never both.
  if (!openOnFullTab()) revealSelection()
  paintTabs()
  paintTiles()
  paintVisibility()
  /*
   * TELL THE HUD ITS UNDO BUTTON HAS A HOME AGAIN.
   *
   * Round-3 critic, blocker: on the first build screen a kid ever sees, the HUD's
   * undo button was rendering in its bottom-left corner fallback UNDERNEATH this
   * drawer — `elementFromPoint` returned "Turn the piece" at all nine sample
   * points inside it, reachFrac 0.00 — because the HUD only re-docks on a mode
   * change and this drawer had not been on screen at the moment that fired.
   * A drawer that has just mounted says so, and the HUD docks into it whatever
   * order the two crews happened to run in.
   */
  bus.emit('build:paletteMounted', { dock: true })
}

export function installPalette(ctx: GameContext): void {
  canvasEl = ctx.canvas
  // The HUD crew clears #hud inside its own install, which runs AFTER ours in
  // main.ts. Mounting on a microtask puts the drawer in after that wipe without
  // reaching into another crew's file or reordering boot.
  queueMicrotask(() => mount(ctx.hud))

  /** If anyone else rebuilds the HUD later, put the drawer back. A kid must
   *  never find build mode with no pieces in it. */
  const ensureMounted = (): void => {
    if (rootEl === null || !rootEl.isConnected) mount(ctx.hud)
  }

  bus.on('app:modeChanged', () => {
    ensureMounted()
    paintVisibility()
    paintTiles()
  })
  /*
   * A PHASE CHANGE CAN PUT THE DRAWER ON SCREEN TOO. The monster creator covers
   * the whole HUD, so the drawer's box is zero while it is up; when the tick is
   * tapped, the drawer appears without any mode change at all. Repaint from
   * here, or the camera's drawer allowance and the HUD's undo dock are both
   * working from a measurement taken while the drawer was invisible — which is
   * exactly how undo ended up stranded under the drawer on the first screen.
   */
  bus.on('app:phaseChanged', () => {
    ensureMounted()
    paintVisibility()
  })
  bus.on('build:paletteToggled', () => paintVisibility())
  bus.on('build:selected', () => {
    revealSelection()
    paintTiles()
  })
  bus.on('economy:unlocked', () => {
    paintTabs()
    paintTiles()
  })
  bus.on('economy:spent', () => paintTiles())
  bus.on('scene:entered', () => {
    page = 0
    // A room a kid has not chosen a tab in opens on ITS fullest tab: the house
    // opens on furniture, the shop on shop fittings. And if what they had armed
    // does not belong in here at all, arm something that does rather than leave
    // the drawer pointing at a piece this room will not take.
    const here = availableDefs()
    const armed = selectedPlaceable()
    const stillHere = armed !== null && here.some((d) => d.id === armed)
    if (!stillHere) kidChose = false
    openOnFullTab()
    paintTabs()
    paintTiles()
  })
  bus.on('save:loaded', () => {
    paintTabs()
    paintTiles()
  })
}

/** Harness/debug: which piece ids this tab holds, on-screen or not. */
export function paletteContents(): PlaceableId[] {
  const tab = currentTab()
  if (tab === undefined) return []
  return defsForTab(tab, availableDefs()).map((d) => d.id)
}

/**
 * Harness/QA: proof that nothing is unreachable. `rendered` are the tiles
 * actually in the DOM right now; `pages` is how many taps of one 88 px arrow it
 * takes to see the rest. `offscreen` must always be 0.
 */
export function paletteLayout(): {
  tab: string
  page: number
  pages: number
  perPage: number
  total: number
  rendered: number
  offscreen: number
} {
  const tab = currentTab()
  const total = tab === undefined ? 0 : defsForTab(tab, availableDefs()).length
  const per = measurePerPage()
  const rendered = tilesEl === null ? 0 : tilesEl.childElementCount
  let offscreen = 0
  if (tilesEl !== null) {
    const box = tilesEl.getBoundingClientRect()
    for (const child of Array.from(tilesEl.children)) {
      const r = child.getBoundingClientRect()
      if (r.right > box.right + 1 || r.left < box.left - 1 || r.bottom > box.bottom + 1) offscreen++
    }
  }
  return {
    tab: activeTab,
    page,
    pages: Math.max(1, Math.ceil(total / Math.max(1, per))),
    perPage: per,
    total,
    rendered,
    offscreen,
  }
}
